import React, { useState, useEffect } from "react";
import { X, Mail, Phone, User, ArrowRight, ShieldCheck, RefreshCw, CheckCircle2, AlertCircle } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { brand } from "../../config/theme";

const AuthModal: React.FC = () => {
  const {
    isAuthModalOpen,
    authModalMode,
    openAuthModal,
    closeAuthModal,
    sendOtp,
    verifyOtpAndLogin,
    verifyOtpAndSignup,
  } = useAuth();

  const [mode, setMode] = useState<"login" | "signup">("login");
  const [step, setStep] = useState<"details" | "otp">("details");

  // Form Fields
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [devOtp, setDevOtp] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);

  // Sync mode with context
  useEffect(() => {
    if (isAuthModalOpen) {
      setMode(authModalMode);
      setStep("details");
      setError(null);
      setSuccessMsg(null);
      setOtp("");
    }
  }, [isAuthModalOpen, authModalMode]);

  // Resend Countdown timer
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => {
      setCountdown((c) => (c > 0 ? c - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  if (!isAuthModalOpen) return null;

  // Handle Send OTP
  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);

    if (!email.trim() || !email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }

    if (mode === "signup") {
      if (!firstName.trim() || !lastName.trim()) {
        setError("Please enter both your First Name and Last Name.");
        return;
      }
    }

    setLoading(true);
    const res = await sendOtp(email.trim(), mode);
    setLoading(false);

    if (res.success) {
      setStep("otp");
      setCountdown(45);
      setSuccessMsg(res.message);
      if (res.dev_otp) {
        setDevOtp(res.dev_otp);
      }
    } else {
      setError(res.message);
    }
  };

  // Handle Verify OTP
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);

    if (!otp.trim() || otp.trim().length !== 6) {
      setError("Please enter the 6-digit verification code sent to your email.");
      return;
    }

    setLoading(true);

    if (mode === "login") {
      const res = await verifyOtpAndLogin(email.trim(), otp.trim());
      setLoading(false);
      if (!res.success) {
        setError(res.message);
      }
    } else {
      const res = await verifyOtpAndSignup({
        email: email.trim(),
        otp: otp.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim() || undefined,
      });
      setLoading(false);
      if (!res.success) {
        setError(res.message);
      }
    }
  };

  // Handle Resend OTP
  const handleResend = async () => {
    if (countdown > 0 || loading) return;
    setError(null);
    setLoading(true);
    const res = await sendOtp(email.trim(), mode);
    setLoading(false);

    if (res.success) {
      setCountdown(45);
      setSuccessMsg("A new verification code has been dispatched.");
      if (res.dev_otp) {
        setDevOtp(res.dev_otp);
      }
    } else {
      setError(res.message);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={closeAuthModal}
        aria-hidden="true"
      />

      {/* Modal Dialog */}
      <div
        className="relative w-full max-w-[460px] overflow-hidden rounded-3xl bg-white p-8 shadow-2xl transition-all"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={closeAuthModal}
          className="absolute right-6 top-6 flex h-8 w-8 items-center justify-center rounded-full text-[#696969] transition-colors hover:bg-[#f5f5f2] hover:text-black"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        {/* Brand Header */}
        <div className="text-center">
          <span className="text-2xl font-semibold italic text-[#2b3823]">{brand.name}</span>
          <p className="mt-1 text-[13px] text-[#696969]">
            {mode === "login"
              ? "Sign in to access your saved addresses, orders & wishlist."
              : "Create your Meoora account for seamless clean beauty checkout."}
          </p>
        </div>

        {/* Mode Switcher Tabs */}
        <div className="mt-6 flex rounded-full bg-[#f5f5f2] p-1">
          <button
            type="button"
            onClick={() => {
              setMode("login");
              setStep("details");
              setError(null);
              setSuccessMsg(null);
            }}
            className={`flex-1 rounded-full py-2 text-[14px] font-medium transition-all ${
              mode === "login"
                ? "bg-white text-black shadow-sm"
                : "text-[#696969] hover:text-black"
            }`}
          >
            Sign In
          </button>
          <button
            type="button"
            onClick={() => {
              setMode("signup");
              setStep("details");
              setError(null);
              setSuccessMsg(null);
            }}
            className={`flex-1 rounded-full py-2 text-[14px] font-medium transition-all ${
              mode === "signup"
                ? "bg-white text-black shadow-sm"
                : "text-[#696969] hover:text-black"
            }`}
          >
            Create Account
          </button>
        </div>

        {/* Notifications */}
        {error && (
          <div className="mt-4 flex items-start gap-2.5 rounded-2xl bg-red-50 p-3.5 text-[13px] text-red-700">
            <AlertCircle size={17} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {successMsg && !error && (
          <div className="mt-4 flex items-start gap-2.5 rounded-2xl bg-[#e1f2c5] p-3.5 text-[13px] text-green-darker">
            <CheckCircle2 size={17} className="shrink-0 mt-0.5 text-green-accent" />
            <span>{successMsg}</span>
          </div>
        )}

        {/* Dev OTP Helper if available in dev */}
        {devOtp && step === "otp" && (
          <div className="mt-3 flex items-center justify-between rounded-xl border border-dashed border-green-muted bg-[#f4f7ee] px-3.5 py-2 text-[12px] text-green-darker">
            <span>Dev Verification Code: <strong className="font-mono text-[14px]">{devOtp}</strong></span>
            <button
              type="button"
              onClick={() => setOtp(devOtp)}
              className="text-[11px] font-semibold underline hover:text-black"
            >
              Fill Code
            </button>
          </div>
        )}

        {/* STEP 1: Details / Email Input */}
        {step === "details" && (
          <form onSubmit={handleSendOtp} className="mt-6 space-y-4">
            {mode === "signup" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[13px] font-medium text-[#333]">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative mt-1">
                    <User size={16} className="absolute left-3.5 top-3 text-[#999]" />
                    <input
                      type="text"
                      required
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="e.g. Mira"
                      className="w-full rounded-2xl border border-[#dcdcdc] bg-[#fafaf8] pl-10 pr-3.5 py-2.5 text-[14px] outline-none transition-colors focus:border-black focus:bg-white"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-[#333]">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative mt-1">
                    <User size={16} className="absolute left-3.5 top-3 text-[#999]" />
                    <input
                      type="text"
                      required
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="e.g. Kapoor"
                      className="w-full rounded-2xl border border-[#dcdcdc] bg-[#fafaf8] pl-10 pr-3.5 py-2.5 text-[14px] outline-none transition-colors focus:border-black focus:bg-white"
                    />
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="block text-[13px] font-medium text-[#333]">
                Email Address <span className="text-red-500">*</span>
              </label>
              <div className="relative mt-1">
                <Mail size={16} className="absolute left-3.5 top-3 text-[#999]" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@example.com"
                  className="w-full rounded-2xl border border-[#dcdcdc] bg-[#fafaf8] pl-10 pr-3.5 py-2.5 text-[14px] outline-none transition-colors focus:border-black focus:bg-white"
                />
              </div>
              <p className="mt-1 text-[11px] text-[#909090]">
                We will send a 6-digit verification code to this email.
              </p>
            </div>

            {mode === "signup" && (
              <div>
                <label className="block text-[13px] font-medium text-[#333]">
                  Phone Number <span className="text-[#999] text-[11px]">(Optional for delivery SMS)</span>
                </label>
                <div className="relative mt-1">
                  <Phone size={16} className="absolute left-3.5 top-3 text-[#999]" />
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    placeholder="10-digit mobile number"
                    className="w-full rounded-2xl border border-[#dcdcdc] bg-[#fafaf8] pl-10 pr-3.5 py-2.5 text-[14px] outline-none transition-colors focus:border-black focus:bg-white"
                  />
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn btn-dark mt-2 w-full !py-3.5 text-[15px] font-medium disabled:opacity-50"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <RefreshCw size={16} className="animate-spin" /> Sending Code...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Send Verification Code <ArrowRight size={16} />
                </span>
              )}
            </button>
          </form>
        )}

        {/* STEP 2: OTP Verification */}
        {step === "otp" && (
          <form onSubmit={handleVerifyOtp} className="mt-6 space-y-4">
            <div className="rounded-2xl bg-[#f5f5f2] p-4 text-center">
              <span className="text-[12px] text-[#696969]">Code sent to:</span>
              <p className="font-semibold text-black">{email}</p>
              <button
                type="button"
                onClick={() => setStep("details")}
                className="mt-1 text-[12px] text-green-accent underline hover:text-green-darker"
              >
                Change email address
              </button>
            </div>

            <div>
              <label className="block text-center text-[13px] font-medium text-[#333]">
                Enter 6-Digit Verification Code
              </label>
              <input
                type="text"
                autoFocus
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="• • • • • •"
                className="mt-2 w-full rounded-2xl border border-[#dcdcdc] bg-[#fafaf8] py-3 text-center font-mono text-2xl tracking-[8px] outline-none transition-colors focus:border-black focus:bg-white"
              />
            </div>

            <button
              type="submit"
              disabled={loading || otp.length !== 6}
              className="btn btn-dark w-full !py-3.5 text-[15px] font-medium disabled:opacity-50"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <RefreshCw size={16} className="animate-spin" /> Verifying...
                </span>
              ) : (
                <span>{mode === "signup" ? "Verify & Create Account" : "Verify & Sign In"}</span>
              )}
            </button>

            <div className="flex items-center justify-between pt-2 text-[13px] text-[#696969]">
              <span>Didn&apos;t receive the code?</span>
              {countdown > 0 ? (
                <span className="font-mono text-[#999]">Resend in {countdown}s</span>
              ) : (
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={loading}
                  className="font-medium text-green-accent underline hover:text-green-darker disabled:opacity-50"
                >
                  Resend Code
                </button>
              )}
            </div>
          </form>
        )}

        {/* Security footer */}
        <div className="mt-8 border-t border-[#eee] pt-4 text-center">
          <p className="flex items-center justify-center gap-1.5 text-[11px] text-[#909090]">
            <ShieldCheck size={14} className="text-green-dark" />
            Passwordless single-use OTP authentication protects your data.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
